import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "./cn";

/**
 * Yıkıcı bir aksiyonu onaylatan diyalog — `window.confirm`'ün yerine geçer.
 *
 * Neden var: native confirm tek bir soru sorabiliyor, oysa bazı silmelerin
 * **ikinci bir kararı** oluyor ("misafirin medyası da silinsin mi?"). O karar
 * sessiz bir yan etki olarak bırakıldığında kullanıcı ne kaybettiğini
 * göremiyor; bu bileşen etkiyi yazıyla söyler ve seçimi açık hale getirir.
 *
 * Organizer + admin panelleri aynı bileşeni kullanır (yetki ayrımı çağıranda).
 * Metinlerin tamamı çağırandan gelir — paket i18n'e bağlı değildir.
 */
export interface ConfirmDialogOption {
  /** Kutunun işaretli olup olmadığı — kontrol çağıranda. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Seçimin sonucunu açıklayan kısa metin (işaretli/işaretsiz durumlarına göre). */
  hint?: string;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Ana açıklama — ne olacağını düz cümleyle söyler. */
  description?: string;
  /** İsteğe bağlı ek karar (ör. "medyası da silinsin"). */
  option?: ConfirmDialogOption;
  confirmLabel: string;
  cancelLabel: string;
  /** true → onay butonu kırmızı (yıkıcı aksiyon). */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open, title, description, option,
  confirmLabel, cancelLabel, destructive = true, busy = false,
  onConfirm, onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Odak iptal butonunda başlar: yıkıcı aksiyon yanlışlıkla Enter'lanmasın.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-6 py-5">
          <div className={cn(
            "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full",
            destructive ? "bg-red-500/10 text-red-500" : "bg-primary/10 text-primary",
          )}>
            <AlertTriangle className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            {description && (
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label={cancelLabel}
            className="size-8 shrink-0 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground cursor-pointer transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {option && (
          <div className="mx-6 mb-5 rounded-xl bg-muted/40 p-4">
            <label className="flex cursor-pointer select-none items-start gap-2.5">
              <input
                type="checkbox"
                checked={option.checked}
                onChange={(e) => option.onChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-red-500"
              />
              <span className="text-sm font-medium text-foreground">{option.label}</span>
            </label>
            {option.hint && (
              <p className="mt-1.5 pl-6.5 text-xs text-muted-foreground leading-relaxed">{option.hint}</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-border px-5 py-2 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              "rounded-full px-5 py-2 text-sm font-semibold text-white transition-colors cursor-pointer disabled:opacity-50",
              destructive ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:bg-primary/90",
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
